import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useState } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--jira-gryffindor/attachment-consolidation-add-create';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { Text } from '@atlaskit/primitives/compiled';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { defineMessages, FormattedMessage } from 'react-intl';

import { type JSX } from 'react/jsx-runtime';

import { type MessageTemplateContext } from '../../../index';

const messages = defineMessages({
	headline: {
		id: 'post-office.attachment-consolidation-add-create.spotlight.headline',
		defaultMessage: 'Confluence and Loom are now in Attachments',
		description:
			'Heading for a spotlight in Jira issue attachments announcing that Confluence and Loom content can now be found in the Attachments area.',
	},
	body: {
		id: 'post-office.attachment-consolidation-add-create.spotlight.body',
		defaultMessage:
			'Find your pages, videos, and files in one place. You can search for existing content or upload new files directly.',
		description:
			'Body text for a spotlight in Jira issue attachments explaining that users can find Confluence pages, Loom videos, and uploaded files together.',
	},
	gotIt: {
		id: 'post-office.attachment-consolidation-add-create.spotlight.got-it',
		defaultMessage: 'Got it',
		description:
			'Primary action on a Jira issue attachments spotlight. Selecting it acknowledges and dismisses the spotlight.',
	},
});

type RenderProps = MessageTemplateContext & PlacementRenderProps;

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: RenderProps) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss, placement }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onClosed, onAcknowledged } = usePostOfficeUserInteractions();
		const [isVisible, setIsVisible] = useState(true);

		const handleDismiss = useCallback(() => {
			setIsVisible(false);
			void messageDismissed();
			void onClosed({ interactionIdentifier: 'dismiss' });
			onDismiss?.();
		}, [messageDismissed, onClosed, onDismiss]);

		const handleGotIt = useCallback(() => {
			setIsVisible(false);
			void messageDismissed();
			void onAcknowledged({
				interactionIdentifier: 'got-it',
				additionalMetadata: { actionTaken: true },
			});
			onDismiss?.();
		}, [messageDismissed, onAcknowledged, onDismiss]);

		useEffect(() => {
			if (!isVisible) {
				return;
			}

			const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
				if (keyboardEvent.key === 'Escape') {
					handleDismiss();
				}
			};

			globalThis.document?.addEventListener('keydown', handleKeyDown);
			return () => globalThis.document?.removeEventListener('keydown', handleKeyDown);
		}, [handleDismiss, isVisible]);

		if (!isVisible) {
			return null;
		}

		return (
			<PopoverContent
				testId="attachment-consolidation-add-create.spotlight"
				isVisible
				placement={placement}
				dismiss={handleDismiss}
				shouldDismissOnClickOutside
			>
				<SpotlightCard>
					<SpotlightHeader>
						<SpotlightHeadline>
							<FormattedMessage {...messages.headline} />
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleDismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>
							<FormattedMessage {...messages.body} />
						</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleGotIt}>
								<FormattedMessage {...messages.gotIt} />
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
